/**
 * Upload Audit Data Modal Component
 * Modal for uploading audit data from Excel or JSON files
 */

import React, { useState } from 'react';
import { Modal, Upload, Button, message, Alert, Space, Spin } from 'antd';
import { UploadOutlined, FileExcelOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { UploadFile } from 'antd/es/upload/interface';
import { ExcelParserService } from '../../../services/excel/excelParserService';
import type { AuditGeneralData, ProducerData, PlotData } from '../../../services/excel/types';
import { auditService } from '../../../services/api';
import { API_BASE_URL } from '../../../utils/constants';

export interface UploadAuditDataModalProps {
  visible: boolean;
  onClose: () => void;
  auditId: number;
  auditType: number; // 1 = Complete, 2 = Yield
  onSuccess: () => void;
}

interface UploadState {
  file: File | null;
  fileType: 'excel' | 'json' | null;
  uploading: boolean;
  processing: boolean;
  error: string | null;
  success: boolean;
}

export const UploadAuditDataModal: React.FC<UploadAuditDataModalProps> = ({
  visible,
  onClose,
  auditId,
  auditType,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [state, setState] = useState<UploadState>({
    file: null,
    fileType: null,
    uploading: false,
    processing: false,
    error: null,
    success: false,
  });

  const excelParser = new ExcelParserService();

  const beforeUpload = (file: File) => {
    const isExcel =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.name.toLowerCase().endsWith('.xlsx');
    const isJson =
      file.type === 'application/json' ||
      file.name.toLowerCase().endsWith('.json');

    if (!isExcel && !isJson) {
      message.error('El archivo debe ser Excel (.xlsx) o JSON (.json)');
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('El tamaño del archivo debe ser menor a 10MB');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList].slice(-1);
    setFileList(newFileList);

    const file = newFileList[0]?.originFileObj || null;
    let fileType: 'excel' | 'json' | null = null;
    if (file) {
      fileType = file.name.toLowerCase().endsWith('.json') ? 'json' : 'excel';
    }

    setState({
      file,
      fileType,
      uploading: false,
      processing: false,
      error: null,
      success: false,
    });
  };

  const processExcelFile = async (file: File): Promise<{
    audit_data: AuditGeneralData;
    producer_data: ProducerData;
    plots_data: PlotData[];
    file_base64: string;
  }> => {
    try {
      const workbook = await excelParser.readExcelFile(file);
      const audit_data = excelParser.parseAuditData(workbook, auditType);
      const producer_data = excelParser.parseProducerData(workbook, auditType);
      const plots_data = excelParser.parsePlotsData(workbook, auditType);

      const validation = excelParser.validateRequiredFields(
        { audit_data, producer_data, plots_data },
        auditType
      );
      if (!validation.valid) {
        throw new Error(`Errores de validación:\n${validation.errors.join('\n')}`);
      }

      const file_base64 = await excelParser.fileToBase64(file);
      return { audit_data, producer_data, plots_data, file_base64 };
    } catch (error) {
      throw new Error(
        `Error procesando archivo Excel: ${error instanceof Error ? error.message : 'Error desconocido'}`
      );
    }
  };

  const processJsonFile = async (file: File): Promise<Record<string, unknown>> => {
    const text = await file.text();
    try {
      return JSON.parse(text);
    } catch {
      throw new Error('El archivo no contiene JSON válido');
    }
  };

  const handleSubmit = async () => {
    if (fileList.length === 0 || !state.file) {
      message.error('Por favor seleccione un archivo');
      return;
    }
    if (state.uploading || state.processing) return;

    setState((prev) => ({ ...prev, processing: true, error: null, success: false }));

    try {
      if (state.fileType === 'json') {
        // JSON flow: read file, send raw JSON to backend
        const jsonData = await processJsonFile(state.file);

        setState((prev) => ({ ...prev, processing: false, uploading: true }));

        const token = localStorage.getItem('access_token') || '';
        const res = await fetch(`${API_BASE_URL}/audits/${auditId}/upload-json-data/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(jsonData),
        });

        const result = await res.json();
        if (!res.ok || !result.success) {
          // Flatten the same shapes the Excel service flattens so
          // backend validation errors are readable in the modal.
          let parts: string[] = [];
          if (Array.isArray(result.errors)) {
            parts = result.errors.map((e: any) => String(e));
          } else if (result.errors && typeof result.errors === 'object') {
            for (const [field, val] of Object.entries(result.errors)) {
              if (Array.isArray(val)) {
                for (const msg of val) parts.push(`${field}: ${msg}`);
              } else {
                parts.push(`${field}: ${String(val)}`);
              }
            }
          }
          if (parts.length === 0) {
            parts = [result.message || 'Error al cargar JSON'];
          }
          throw new Error(parts.join('\n'));
        }

        message.success(result.message || 'Datos JSON cargados exitosamente');
        setState((prev) => ({ ...prev, uploading: false, success: true }));
        setFileList([]);
        setTimeout(() => { onSuccess(); onClose(); }, 1500);
      } else {
        // Excel flow: parse in frontend, send structured data
        const extractedData = await processExcelFile(state.file);
        setState((prev) => ({ ...prev, processing: false, uploading: true }));

        try {
          const response = await auditService.uploadAuditData(auditId, {
            audit_type: auditType,
            ...extractedData,
          });
          message.success(response.message || 'Datos de auditoría cargados exitosamente');
          setState((prev) => ({ ...prev, uploading: false, success: true }));
          setFileList([]);
          setTimeout(() => { onSuccess(); onClose(); }, 1500);
        } catch (error: any) {
          // The service already flattens the backend payload into
          // `error.errors: string[]`. We surface that in the
          // in-modal Alert; the toast is intentionally suppressed
          // so the message lives in one place.
          const errorMessage =
            error?.errors?.join('\n') || error?.message || 'Error al cargar datos';
          setState((prev) => ({ ...prev, uploading: false, error: errorMessage }));
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setState((prev) => ({
        ...prev,
        processing: false,
        uploading: false,
        error: errorMessage,
      }));
    }
  };

  const handleCancel = () => {
    if (state.uploading || state.processing) {
      message.warning('Por favor espere a que termine el proceso actual');
      return;
    }
    setFileList([]);
    setState({ file: null, fileType: null, uploading: false, processing: false, error: null, success: false });
    onClose();
  };

  const isProcessing = state.processing || state.uploading;
  const canSubmit = fileList.length > 0 && !isProcessing;

  return (
    <Modal
      title="Cargar Datos de Auditoría"
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Cargar"
      cancelText="Cancelar"
      confirmLoading={isProcessing}
      okButtonProps={{ disabled: !canSubmit }}
      width={600}
      maskClosable={!isProcessing}
      closable={!isProcessing}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Error alert sits at the top so backend validation errors
            don't get pushed below the fold when the message is long
            or multi-line. */}
        {state.error && (
          <Alert
            message="No se pudo cargar el archivo"
            description={
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {state.error}
              </div>
            }
            type="error"
            showIcon
            closable
            onClose={() => setState((prev) => ({ ...prev, error: null }))}
          />
        )}

        <Alert
          message="Instrucciones"
          description="Seleccione un archivo Excel (.xlsx) o JSON (.json) con los datos de la auditoría. El sistema detectará el formato automáticamente."
          type="info"
          showIcon
        />

        <div style={{ padding: '20px 0' }}>
          <Upload
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={handleFileChange}
            maxCount={1}
            accept=".xlsx,.json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json"
            listType="text"
            disabled={isProcessing}
          >
            <Button
              icon={<UploadOutlined />}
              size="large"
              style={{ width: '100%' }}
              disabled={isProcessing}
            >
              {state.fileType === 'json' ? (
                <><FileTextOutlined style={{ marginLeft: 8 }} /> Archivo JSON seleccionado</>
              ) : (
                <><FileExcelOutlined style={{ marginLeft: 8 }} /> Seleccionar Archivo (Excel o JSON)</>
              )}
            </Button>
          </Upload>
          <div style={{ marginTop: 12, color: '#8c8c8c', fontSize: '12px' }}>
            Tamaño máximo: 10MB. Se permiten archivos Excel (.xlsx) y JSON (.json).
          </div>
        </div>

        {state.processing && (
          <Alert message="Procesando archivo..." description="Leyendo y validando datos. Por favor espere." type="info" showIcon icon={<Spin />} />
        )}
        {state.uploading && (
          <Alert message="Enviando datos..." description="Cargando datos al servidor. Por favor espere." type="info" showIcon icon={<Spin />} />
        )}
        {state.success && (
          <Alert message="¡Éxito!" description="Los datos de auditoría se han cargado correctamente." type="success" showIcon icon={<CheckCircleOutlined />} />
        )}
      </Space>
    </Modal>
  );
};
